import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { VoicemailResource } from '../../../shared/resources/voicemail.resource';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { PinReset } from '../../cisco/public-phone/public-phone-pin-reset/public-phone-pin-reset.component';
import { HtmlInputType, ZiroTextConfig } from '../../../forms/fields/text/ziro-text.component';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { HelpdeskOptionsContext } from '../../shared/contexts/helpdesk-options.context';
import { CiscoHelpdeskOptions, Voicemail } from '../../../shared/models/generated/ziroModels';
import { Observable, of, Subscription } from 'rxjs';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { HttpErrorResponse } from '@angular/common/http';
import { PinResetService } from '../../../shared/services/pin-reset.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ZiroIcons, ZiroStackedIcons } from '../../../shared/models/ziro-icons.enum';
import { PinResetComponent } from '../../cisco/pin-reset/pin-reset.component';
import { isEqual } from 'lodash';

@Component({
  selector: 'ziro-search-result-pin-reset',
  templateUrl: './search-result-pin-reset.component.html',
  standalone: false,
})
export class SearchResultPinResetComponent extends ZiroFormAbstractDirective<PinReset> implements OnInit {
  private _route = inject(ActivatedRoute);
  private _pinResetService = inject(PinResetService);
  private _toastService = inject(ToastService);
  private _voicemailResource = inject(VoicemailResource);
  private _helpdeskOptionsContext = inject(HelpdeskOptionsContext);
  protected _ziroFormStateService: ZiroFormStateService;

  @ViewChild('vmPin') vmPinResetComponent: PinResetComponent;

  ZiroStackedIcons = ZiroStackedIcons;
  ziroIcons = ZiroIcons;
  isLoading = true;
  voicemail: Voicemail;
  errorMessage = '';
  formConfig = {
    fields: {
      vmPinReset: {
        dataAutomation: 'voicemail-pin-reset',
        label: 'tkey;pages.pinreset.voicemail',
        componentConfig: new ZiroTextConfig({
          htmlInputType: HtmlInputType.PASSWORD,
          placeholder: 'tkey;pages.reset.pin.placeholder',
        }),
        validation: [
          {
            validator: (val: string) => {
              return !val || (!!val && /(^[0-9]+$)/.test(val))
                ? ZiroFormsValidationState.VALID
                : ZiroFormsValidationState.INVALID;
            },
            message: 'tkey;validators.global.error.pattern',
          },
          {
            validator: () => (this._hasError ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID),
            message: () => this.errorMessage,
          },
        ],
      },
    },
  } as ZiroFormConfig;
  private _resetPinOnNextLogin = false;
  private _hasError = false;
  private _subscriptions = new Subscription();
  private _serverId: string;

  ngOnInit(): void {
    this._serverId = this._route.snapshot.paramMap.get('serverId');
    const voicemailId = this._route.snapshot.paramMap.get('voicemailId');

    const helpdeskOptSub = this._helpdeskOptionsContext.state$.subscribe((helpdeskOptions) => {
      this._resetPinOnNextLogin = (helpdeskOptions as CiscoHelpdeskOptions).resetPinOnNextLoginEnabled;

      this._voicemailResource.get(Number(this._serverId), voicemailId).subscribe((voicemail) => {
        this.voicemail = voicemail;
        this.isLoading = false;
      });
    });
    this._subscriptions.add(helpdeskOptSub);

    this.ziroFormsUpdate$.subscribe((data: ZiroFormsUpdate<PinReset>) => {
      if (data.old?.vmPinReset && !isEqual(data.new.vmPinReset, data.old.vmPinReset) && this._hasError) {
        this._hasError = false;
      }
    });
  }

  submit(): Observable<null> {
    return of(null);
  }

  onSubmit(): void {
    this._resetVoicemailPin();
  }

  private _resetVoicemailPin(): void {
    this._pinResetService
      .resetVoicemailWithServerId(
        Number(this._serverId),
        this.voicemail,
        this.entity.vmPinReset,
        this._resetPinOnNextLogin,
      )
      .subscribe({
        next: () => {
          this._toastService.push(
            ToastTypes.SUCCESS,
            this.ziroIcons.REFRESH,
            'tkey;pages.pinreset.success.title',
            'tkey;pages.pinreset.voicemail.success.message',
          );
          this._resetVoicemailPinInput();
        },
        error: (error) => {
          if (error.status === 422) {
            this._hasError = true;
            this.errorMessage = this._pinResetService.translateVoicemailPinResetErrorResponse(error);
            this.fieldChannels['vmPinReset'].validateSource.next();
          } else {
            throw new HttpErrorResponse(error);
          }
          this._resetVoicemailPinInput();
        },
      });
  }

  private _resetVoicemailPinInput(): void {
    this.vmPinResetComponent.isPending = false;
    this.entitySource.next({
      vmPinReset: '',
    });
    this._ziroFormStateService.setIsFormDirty(false);
  }
}
